/**
 * Echo Hunt -- the seed-word list (P6, §5.2 + §6).
 *
 * ## Where the data comes from
 *
 * The project ALREADY ships a concreteness word list, which `Pkg/client/Resources/JSON/@Notes.txt`
 * documents as derived from Brysbaert, Warriner & Kuperman (2014), "Concreteness ratings for 40
 * thousand generally known English word lemmas" (https://pubmed.ncbi.nlm.nih.gov/24142837/), via the
 * archived `crr.ugent.be` TSV and the `TSVToJSON.js` converter.
 *
 * Echo Hunt reuses that asset rather than bundling a second word list (§16.2 P6 originally proposed a
 * new `Source/EchoHunt/Data/Wordlist.json`; that was superseded once it turned out the annotated list
 * already exists):
 *
 *   `/JSON/WordConcreteness_Sorted_Tagged.json`
 *     - a header row, then one row per lemma:
 *       `[Word, Bigram, Conc.M, Conc.SD, Unknown, Total, Percent_known, SUBTLEX, Dom_Pos, Tags]`
 *     - `Tags` is a comma-separated string, and it is a MANUAL annotation added by this project's
 *       author, not part of the published dataset.
 *
 * The same file is already loaded (and cached) at runtime by
 * `Engine/LiveSession/Components/Visualization/WordLists.ts`; `Wordlist_Live.ts` reuses that loader so
 * there is exactly one fetch and one cache.
 *
 * ## The manual tags
 *
 * Scanning the list by hand, three tags were applied:
 *   - `BadResolve`  -- hard or slow to visualize (ambiguous variant, or too fringe a word)
 *   - `BadCrawl`    -- annoying to integrate into a scene (too large, or part of a person/thing,
 *                      which forces a decision about its owner's appearance)
 *   - `NSFW`        -- self-explanatory; also covers things commonly considered unpleasant
 *
 * IMPORTANT: the manual tagging was only done for entries with `Conc.M >= 4.48`. That is why
 * `WORDLIST_TAG_COVERAGE_CONCRETENESS_MIN` exists and why `FilterWordlist` emits a loud warning when a
 * caller lowers the concreteness floor below it: below that point the tags no longer describe the
 * whole included set, so "NSFW/BadCrawl are excluded" would silently stop being true.
 *
 * ## The cutoffs the author used
 *
 *   - `Conc.M >= 4.48`
 *   - `Percent_known >= .95`
 *
 * which yields 4763 words before tag filtering, and 1596 after excluding all three tags (both counts
 * reproduce `FilterWordlist` with the defaults below, and are pinned by tests as a non-triviality
 * guard).
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). The fetch/cache lives in
 * `Wordlist_Live.ts`.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §5.2, §6, §16.2 P6.
 */

import type {EchoWordlistSettings} from "./Types.js";

// constants
// ==========

/** Lower `Conc.M` bound the author used when filtering, and -- for exactly that reason -- the
 * smallest concreteness floor at which the manual tags still cover every included entry. */
export const WORDLIST_TAG_COVERAGE_CONCRETENESS_MIN = 4.48;

/** Default `Conc.M` cutoff (the author's). */
export const WORDLIST_DEFAULT_CONCRETENESS_MIN = 4.48;
/** Default `Percent_known` cutoff (the author's). */
export const WORDLIST_DEFAULT_PERCENT_KNOWN_MIN = .95;

export const WORDLIST_TAG_BAD_RESOLVE = "BadResolve";
export const WORDLIST_TAG_BAD_CRAWL = "BadCrawl";
export const WORDLIST_TAG_NSFW = "NSFW";

/** Every tag this module understands. Used by the Settings UI to offer the valid values, and to warn
 * about unrecognized tags rather than silently ignoring a typo. */
export const WORDLIST_KNOWN_TAGS = [
	WORDLIST_TAG_BAD_RESOLVE,
	WORDLIST_TAG_BAD_CRAWL,
	WORDLIST_TAG_NSFW,
] as const;

/** Tags excluded by default (§5.2: seeds must be cheap and quick to visualize/integrate, and never
 * NSFW). */
export const WORDLIST_DEFAULT_EXCLUDED_TAGS = [
	WORDLIST_TAG_BAD_RESOLVE,
	WORDLIST_TAG_BAD_CRAWL,
	WORDLIST_TAG_NSFW,
];

/** Sleep/dream vocabulary, always excluded (§6: a seed word must not hand the generator the one
 * subject the whole game is about). Matched with the same inflection-aware rule as banned words. */
export const WORDLIST_SLEEP_DREAM_WORDS = [
	"dream",
	"dreams",
	"dreaming",
	"dreamt",
	"dreamed",
	"sleep",
	"sleeping",
	"slept",
	"wake",
	"waking",
	"woke",
	"awake",
	"nightmare",
	"lucid",
	"lucidity",
];

// types
// ==========

/** One row of `WordConcreteness_Sorted_Tagged.json`, parsed. */
export type WordlistEntry = {
	word: string;
	bigram: boolean;
	/** `Conc.M` -- mean concreteness rating, 1..5 */
	concM: number;
	/** `Percent_known` -- share of raters who reported knowing the word, 0..1 */
	percentKnown: number;
	/** `SUBTLEX` -- subtitle-corpus frequency (words per million) */
	subtlex: number;
	/** `Dom_Pos` -- dominant part of speech ("Noun", "Verb", "#N/A", ...) */
	domPos: string;
	/** the project's manual tags, possibly empty */
	tags: string[];
};

export type WordlistFilterOptions = {
	/** minimum `Conc.M`; defaults to `WORDLIST_DEFAULT_CONCRETENESS_MIN` */
	concretenessMin?: number|n;
	/** minimum `Percent_known`; defaults to `WORDLIST_DEFAULT_PERCENT_KNOWN_MIN` */
	percentKnownMin?: number|n;
	/** tags whose presence anywhere in a word's variant group excludes it (§5.2) */
	excludedTags?: string[]|n;
	/** extra words to drop outright -- the user's banned terms (§4.4) */
	extraExcludedWords?: string[]|n;
	/** merge `word`/`wo-rd` style variants into one entry; default true */
	mergeDuplicates?: boolean|n;
	/** drop sleep/dream vocabulary; default true (set false only in tests, §6) */
	excludeSleepWords?: boolean|n;
};

export type WordlistFilterStats = {
	/** rows in the parsed asset */
	totalEntries: number;
	/** distinct words after variant merging, before any filtering */
	totalGroups: number;
	/** groups dropped because no variant met BOTH numeric cutoffs */
	excludedByCutoffs: number;
	/** groups dropped because at least one variant carried an excluded tag */
	excludedByTags: number;
	/** groups dropped because the word is a banned term or sleep/dream vocabulary */
	excludedByWords: number;
	/** groups that survived */
	resultCount: number;
	/** tags seen on surviving candidates that this module does not recognise (typo guard) */
	unknownTagsSeen: string[];
};

export type WordlistFilterResult = {
	/** the usable seed words, in the asset's own (concreteness-descending) order */
	words: string[];
	stats: WordlistFilterStats;
	/** warnings that are NOT the tag-coverage one; safe to render as a normal note */
	warnings: string[];
	/** true when the concreteness floor is below the manual tagging's coverage (§ above) */
	hasTagCoverageWarning: boolean;
	/** the tag-coverage warning text, kept SEPARATE from `warnings` so a UI can render it loudly
	 * without having to string-match it back out of a list */
	tagCoverageWarning?: string|n;
};

// parsing
// ==========

/** Parses the raw JSON array-of-arrays (header row first). Tolerant where the real hand-edited asset
 * requires it: numeric columns may be strings, `Tags` may be empty, and a bad row is skipped rather
 * than throwing (one typo must not take out the whole list). */
export function ParseWordlistJSON(raw: unknown): WordlistEntry[] {
	if (!Array.isArray(raw)) return [];

	const entries: WordlistEntry[] = [];
	for (const row of raw) {
		if (!Array.isArray(row)) continue;
		// skip the header row (its first cell is the literal "Word")
		if (typeof row[0] != "string" || row[0] == "Word") continue;

		const word = row[0].trim();
		if (word.length == 0) continue;

		const concM = ToFiniteNumber(row[2]);
		const percentKnown = ToFiniteNumber(row[6]);
		if (concM == null || percentKnown == null) continue;

		const rawTags = typeof row[9] == "string" ? row[9] : "";
		entries.push({
			word,
			bigram: ToFiniteNumber(row[1]) == 1,
			concM,
			percentKnown,
			subtlex: ToFiniteNumber(row[7]) ?? 0,
			domPos: typeof row[8] == "string" ? row[8].trim() : "",
			tags: rawTags.split(",").map(a=>a.trim()).filter(a=>a.length > 0),
		});
	}
	return entries;
}

function ToFiniteNumber(value: unknown): number|n {
	if (typeof value == "number") return Number.isFinite(value) ? value : null;
	if (typeof value != "string" || value.trim().length == 0) return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

// filtering
// ==========

/** The comparison key that merges word variants (`swing set`, `swingset`, `swing-set` -> one word). */
export function WordVariantKey(word: string): string {
	return word.toLowerCase().replace(/[\s-]/g, "");
}

/** Filters the parsed list to usable seed words. Variant groups (same `WordVariantKey`) are judged as
 * a unit -- in if one variant meets both cutoffs, out if ANY variant carries an excluded tag -- and
 * banned/sleep words are matched on the group key. Same semantics as the visualization word pool. */
export function FilterWordlist(entries: WordlistEntry[], options: WordlistFilterOptions = {}): WordlistFilterResult {
	const concretenessMin = options.concretenessMin ?? WORDLIST_DEFAULT_CONCRETENESS_MIN;
	const percentKnownMin = options.percentKnownMin ?? WORDLIST_DEFAULT_PERCENT_KNOWN_MIN;
	const excludedTags = options.excludedTags ?? WORDLIST_DEFAULT_EXCLUDED_TAGS;
	const mergeDuplicates = options.mergeDuplicates ?? true;
	const excludeSleepWords = options.excludeSleepWords ?? true;

	const warnings: string[] = [];
	const hasTagCoverageWarning = concretenessMin < WORDLIST_TAG_COVERAGE_CONCRETENESS_MIN;
	const tagCoverageWarning = hasTagCoverageWarning
		? `The manual BadResolve/BadCrawl/NSFW tagging was only applied to words with Conc.M >= ${WORDLIST_TAG_COVERAGE_CONCRETENESS_MIN}.`
			+ ` Your concreteness floor is ${concretenessMin}, so part of the included set is UNTAGGED --`
			+ ` words that are slow to visualize or unpleasant can get through.`
		: null;

	// banned terms + sleep words, as variant keys (so hyphen/space variants are all dropped)
	const excludedWordKeys = new Set<string>();
	for (const word of options.extraExcludedWords ?? []) {
		const key = WordVariantKey(word.trim());
		if (key.length > 0) excludedWordKeys.add(key);
	}
	if (excludeSleepWords) {
		for (const word of WORDLIST_SLEEP_DREAM_WORDS) excludedWordKeys.add(WordVariantKey(word));
	}

	// group by variant key, preserving first-seen order
	const groups = new Map<string, WordlistEntry[]>();
	for (const entry of entries) {
		const key = mergeDuplicates ? WordVariantKey(entry.word) : entry.word;
		const existing = groups.get(key);
		if (existing != null) existing.push(entry);
		else groups.set(key, [entry]);
	}

	const words: string[] = [];
	const unknownTagsSeen = new Set<string>();
	let excludedByCutoffs = 0;
	let excludedByTags = 0;
	let excludedByWords = 0;

	for (const [key, group] of groups) {
		// numeric cutoffs: at least one variant must satisfy both at once
		if (!group.some(entry=>entry.concM >= concretenessMin && entry.percentKnown >= percentKnownMin)) {
			excludedByCutoffs++;
			continue;
		}

		// tags: every variant must be clean
		const groupTags = new Set<string>(group.flatMap(a=>a.tags));
		for (const tag of groupTags) {
			if (!(WORDLIST_KNOWN_TAGS as readonly string[]).includes(tag)) unknownTagsSeen.add(tag);
		}
		if (excludedTags.some(tag=>groupTags.has(tag))) {
			excludedByTags++;
			continue;
		}

		// banned terms / sleep vocabulary
		if (excludedWordKeys.has(key)) {
			excludedByWords++;
			continue;
		}

		// the longest variant reads best ("swing set" rather than "swingset")
		const representative = [...group].sort((a, b)=>b.word.length - a.word.length)[0];
		words.push(representative.word);
	}

	if (unknownTagsSeen.size > 0) {
		warnings.push(
			`The word list contains tag(s) this build does not know: ${[...unknownTagsSeen].join(", ")}.`
			+ ` They are never excluded automatically -- add them to the excluded-tags list if they should be.`,
		);
	}

	return {
		words,
		stats: {
			totalEntries: entries.length,
			totalGroups: groups.size,
			excludedByCutoffs,
			excludedByTags,
			excludedByWords,
			resultCount: words.length,
			unknownTagsSeen: [...unknownTagsSeen],
		},
		warnings,
		hasTagCoverageWarning,
		tagCoverageWarning,
	};
}

/** Convenience: just the words, for callers that don't care about the stats/warnings. */
export function FilterWordlistToWords(entries: WordlistEntry[], options: WordlistFilterOptions = {}): string[] {
	return FilterWordlist(entries, options).words;
}

/** True when a filter result actually leaves words to draw seeds from. An empty list is a reachable
 * silent failure (a low `percentKnownMin` plus a broad excluded-tags list can filter everything out),
 * and a seedless generator would quietly emit unseeded fakes while still looking like it worked. */
export function WordlistAvailableForGeneration(result: WordlistFilterResult|n): boolean {
	return result != null && result.words.length > 0;
}

// settings (profile-backed, §5.2)
// ==========

/** Default range for how many seed CONCEPTS one fake is built around (§5.2). The `[EXPERIMENT]` note
 * in §5.2 is why this is user-settable: raise `max` to 3+ to see how forcing more unrelated domains
 * into one short event reads. */
export const DEFAULT_SEED_CONCEPT_COUNT_MIN = 1;
export const DEFAULT_SEED_CONCEPT_COUNT_MAX = 2;

/** The cutoffs a fresh profile starts with -- exactly the ones the manual tags were built with, so
 * every included word is tagged. */
export function DefaultWordlistSettings(): EchoWordlistSettings {
	return {
		concretenessMin: WORDLIST_DEFAULT_CONCRETENESS_MIN,
		percentKnownMin: WORDLIST_DEFAULT_PERCENT_KNOWN_MIN,
		excludedTags: [...WORDLIST_DEFAULT_EXCLUDED_TAGS],
		mergeDuplicates: true,
		seedConceptCountMin: DEFAULT_SEED_CONCEPT_COUNT_MIN,
		seedConceptCountMax: DEFAULT_SEED_CONCEPT_COUNT_MAX,
	};
}

/** Merges a (possibly partial/absent) profile setting with the defaults, so a profile created before
 * this field existed still filters sensibly. `extraExcludedWords` carries the profile's banned terms:
 * a banned term must not be offered as a seed either, or the generator aims straight at it. */
export function FilterWordlistOptionsFromSettings(settings: EchoWordlistSettings|n, extraExcludedWords?: string[]|n): WordlistFilterOptions {
	const defaults = DefaultWordlistSettings();
	return {
		concretenessMin: settings?.concretenessMin ?? defaults.concretenessMin,
		percentKnownMin: settings?.percentKnownMin ?? defaults.percentKnownMin,
		excludedTags: settings?.excludedTags ?? defaults.excludedTags,
		mergeDuplicates: settings?.mergeDuplicates ?? defaults.mergeDuplicates,
		extraExcludedWords: extraExcludedWords ?? [],
	};
}

// sampling (§5.2)
// ==========

/** Picks `count` DISTINCT words using `rng`. Distinct matters: two copies of one word collapse into a
 * single concept while the prompt still claims two. Partial Fisher-Yates over a copy, so the caller's
 * list is untouched and the result is deterministic for a given `rng`. */
export function SampleSeedWords(wordlist: string[], count: number, rng: () => number): string[] {
	const wanted = Math.max(0, Math.min(Math.round(count), wordlist.length));
	const pool = [...wordlist];
	const picked: string[] = [];
	for (let i = 0; i < wanted; i++) {
		const index = Math.min(pool.length - 1, Math.max(0, Math.floor(rng() * pool.length)));
		picked.push(pool[index]);
		// swap-remove keeps this O(count) instead of O(count * listLength)
		pool[index] = pool[pool.length - 1];
		pool.pop();
	}
	return picked;
}
